<script setup>
import { computed, onMounted, defineAsyncComponent } from 'vue'
import { usePage, Link, router } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import draggable from 'vuedraggable'

import { useOverviewStore } from '../../../state/overviewStore.js'
import OverviewKpiGrid     from '../components/ui-components/overview/OverviewKpiGrid.vue'
import OverviewWidget      from '../components/ui-components/overview/OverviewWidget.vue'

const page  = usePage()
const store = useOverviewStore()

// ── Data from page props ─────────────────────────────────────────────────────
const data     = computed(() => page.props.__neo_main?.__data ?? {})
const userName = computed(() => data.value.userName ?? '')

// ── Greeting ─────────────────────────────────────────────────────────────────
const greeting = computed(() => {
    const h = new Date().getHours()
    if (h < 12) return trans('dashboard.greeting_morning')
    if (h < 17) return trans('dashboard.greeting_afternoon')
    return trans('dashboard.greeting_evening')
})

const today = computed(() => {
    return new Date().toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
})

const pendingCount = computed(() => data.value.kpi?.tyden?.cekajici?.value ?? 0)

// ── Async widget components — vytvořené jednou, mimo computed. `trans()' uvnitř
// widgetMap čte reaktivní `activeMessages` z laravel-vue-i18n, takže se widgetMap
// přepočítá znovu ve chvíli, kdy dojdou překlady (viz stejný problém ve wizardu).
// Kdyby defineAsyncComponent() žilo uvnitř computed, dostal by <component :is="...">
// při každém přepočtu novou instanci definice a rozbil by se async loading.
const widgetComponents = {
    pending:      defineAsyncComponent(() => import('../components/ui-components/overview/OverviewPendingQueue.vue')),
    changes:      defineAsyncComponent(() => import('../components/ui-components/overview/OverviewChangeRequests.vue')),
    heatmap:      defineAsyncComponent(() => import('../components/ui-components/overview/OverviewHeatmap.vue')),
    upcoming:     defineAsyncComponent(() => import('../components/ui-components/overview/OverviewUpcoming.vue')),
    workload:     defineAsyncComponent(() => import('../components/ui-components/overview/OverviewWorkload.vue')),
    topforms:     defineAsyncComponent(() => import('../components/ui-components/overview/OverviewTopForms.vue')),
    activity:     defineAsyncComponent(() => import('../components/ui-components/overview/OverviewActivityFeed.vue')),
    quickactions: defineAsyncComponent(() => import('../components/ui-components/overview/OverviewQuickActions.vue')),
}

// ── Widget component map ─────────────────────────────────────────────────────
const widgetMap = computed(() => ({
    pending:      { title: trans('dashboard.widget_pending'),    countKey: 'pendingCount', countVariant: 'warn',  linkLabel: trans('dashboard.link_all'), linkRoute: 'app.reservations', comp: widgetComponents.pending },
    changes:      { title: trans('dashboard.widget_changes'),    countKey: 'changesCount', countVariant: 'brand', linkLabel: trans('dashboard.link_all'), linkRoute: 'app.reservations.requests', comp: widgetComponents.changes },
    heatmap:      { title: trans('dashboard.widget_heatmap'),    subtitle: trans('dashboard.widget_heatmap_sub'),  comp: widgetComponents.heatmap },
    upcoming:     { title: trans('dashboard.widget_upcoming'),   linkLabel: trans('dashboard.link_calendar'), linkRoute: 'app.reservations', comp: widgetComponents.upcoming },
    workload:     { title: trans('dashboard.widget_workload'),   subtitle: trans('dashboard.widget_workload_sub'), comp: widgetComponents.workload },
    topforms:     { title: trans('dashboard.widget_topforms'),   subtitle: trans('dashboard.widget_topforms_sub'), comp: widgetComponents.topforms },
    activity:     { title: trans('dashboard.widget_activity'),   comp: widgetComponents.activity },
    quickactions: { title: trans('dashboard.widget_quickactions'), comp: widgetComponents.quickactions },
}))

const changesCount = computed(() => data.value.changes?.length ?? 0)

function getCount(widgetId) {
    if (widgetId === 'pending')  return pendingCount.value
    if (widgetId === 'changes')  return changesCount.value
    return null
}

// ── Drag & drop masonry ───────────────────────────────────────────────────────
const leftModel = computed({
    get: () => store.visibleLeft,
    set: (val) => { store.leftWidgets = val },
})
const rightModel = computed({
    get: () => store.visibleRight,
    set: (val) => { store.rightWidgets = val },
})

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(() => {
    store.setKpiData(data.value.kpi)
    store.setWorkloadData(data.value.workload)
    store.setHeatmapData(data.value.heatmap)
    store.loadLayout(data.value.settings)
})
</script>

<template>
    <div class="pb-6">
        <OverviewKpiGrid>
            <button
                class="flex items-center gap-2 px-3 py-2 rounded-[10px] text-[13px] font-medium border transition-all"
                :class="store.editMode
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-neutral-300 hover:border-gray-300 dark:hover:border-neutral-600'"
                @click="store.toggleEditMode()"
            >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                {{ $t('dashboard.edit_overview') }}
            </button>

        </OverviewKpiGrid>

            <!-- ── Edit mode: hidden widgets restore panel ── -->
            <div v-if="store.editMode && store.hiddenWidgets.length" class="mb-4 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-100 dark:border-blue-900 rounded-xl flex items-center gap-2 flex-wrap">
                <span class="text-[12px] font-semibold text-blue-600 dark:text-blue-400 mr-1">{{ $t('dashboard.hidden_widgets') }}</span>
                <button
                    v-for="id in store.hiddenWidgets"
                    :key="id"
                    class="text-[12px] px-2.5 py-1 rounded-full bg-white dark:bg-neutral-900 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 hover:bg-blue-600 hover:text-white transition-colors"
                    @click="store.showWidget(id)"
                >+ {{ widgetMap[id]?.title ?? id }}</button>
            </div>

            <!-- ── Masonry: 2 connected draggable columns ── -->
            <div class="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-4 items-start">
                <draggable
                    v-model="leftModel"
                    :item-key="(el) => el"
                    group="widgets"
                    :handle="store.editMode ? '.drag-handle' : undefined"
                    :disabled="!store.editMode"
                    animation="200"
                    class="flex flex-col gap-4"
                    @end="store.saveLayout()"
                >
                    <template #item="{ element }">
                        <OverviewWidget
                            :widget-id="element"
                            :title="widgetMap[element]?.title ?? element"
                            :count="getCount(element)"
                            :count-variant="widgetMap[element]?.countVariant ?? 'brand'"
                            :link-label="widgetMap[element]?.linkLabel ?? null"
                            :link-route="widgetMap[element]?.linkRoute ?? null"
                            :edit-mode="store.editMode"
                            @hide="store.hideWidget"
                        >
                            <component :is="widgetMap[element]?.comp"/>
                        </OverviewWidget>
                    </template>
                </draggable>

                <draggable
                    v-model="rightModel"
                    :item-key="(el) => el"
                    group="widgets"
                    :handle="store.editMode ? '.drag-handle' : undefined"
                    :disabled="!store.editMode"
                    animation="200"
                    class="flex flex-col gap-4"
                    @end="store.saveLayout()"
                >
                    <template #item="{ element }">
                        <OverviewWidget
                            :widget-id="element"
                            :title="widgetMap[element]?.title ?? element"
                            :count="getCount(element)"
                            :count-variant="widgetMap[element]?.countVariant ?? 'brand'"
                            :link-label="widgetMap[element]?.linkLabel ?? null"
                            :link-route="widgetMap[element]?.linkRoute ?? null"
                            :edit-mode="store.editMode"
                            @hide="store.hideWidget"
                        >
                            <component :is="widgetMap[element]?.comp"/>
                        </OverviewWidget>
                    </template>
                </draggable>
            </div>
    </div>
</template>
